import { useState } from "react";
import { ArrowUpDown, Check, ChevronDown, ChevronUp } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Video } from "@/lib/api-types";

export type VideoSortField = "name" | "dateUploaded" | "approvalStatus";
export type VideoSortDirIndex = 0 | 1;
export type VideoSortState = { field: VideoSortField; dirIndex: VideoSortDirIndex };

const FIELD_ORDER: VideoSortField[] = ["name", "dateUploaded", "approvalStatus"];

const FIELD_LABELS: Record<VideoSortField, string> = {
  name: "Name",
  dateUploaded: "Date Uploaded",
  approvalStatus: "Approval Status",
};

const DIRECTION_LABELS: Record<VideoSortField, [string, string]> = {
  name: ["A → Z", "Z → A"],
  dateUploaded: ["Newest → Oldest", "Oldest → Newest"],
  approvalStatus: ["Needs Review → Approved", "Approved → Needs Review"],
};

/** Ordinal rank for approvalStatus sorting — "least resolved" first by default. */
const APPROVAL_RANK: Record<string, number> = {
  changes_requested: 0,
  in_review: 1,
  approved: 2,
};

/** All 3 fields are real — unlike ProjectSortMenu, nothing here is disabled. */
export function sortVideos(videos: Video[], sort: VideoSortState): Video[] {
  const copy = [...videos];
  if (sort.field === "name") {
    copy.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  } else if (sort.field === "dateUploaded") {
    copy.sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
  } else {
    copy.sort(
      (a, b) =>
        (APPROVAL_RANK[a.approvalStatus ?? ""] ?? 99) - (APPROVAL_RANK[b.approvalStatus ?? ""] ?? 99)
    );
  }
  if (sort.dirIndex === 1) copy.reverse();
  return copy;
}

type Props = {
  value: VideoSortState;
  onChange: (next: VideoSortState) => void;
};

/** Same 2-level interaction as ProjectSortMenu, reused for the per-project video list. */
export function VideoSortMenu({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [fieldListOpen, setFieldListOpen] = useState(false);

  const fieldLabel = FIELD_LABELS[value.field];
  const directionLabel = DIRECTION_LABELS[value.field][value.dirIndex];

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setFieldListOpen(false);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground">
          <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={1.75} />
          Sorted by <span className="font-medium text-foreground">{fieldLabel}</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64 p-2">
        <p className="px-1 pb-1.5 text-xs font-medium text-muted-foreground">Sort by</p>

        <button
          type="button"
          onClick={() => setFieldListOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-md bg-secondary px-2.5 py-2 text-sm font-medium text-foreground"
        >
          {fieldLabel}
          {fieldListOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          )}
        </button>

        {fieldListOpen && (
          <div className="mt-1 flex flex-col gap-0.5 rounded-md border border-border py-1">
            {FIELD_ORDER.map((field) => (
              <button
                key={field}
                type="button"
                onClick={() => {
                  onChange({ field, dirIndex: 0 });
                  setFieldListOpen(false);
                }}
                className="flex items-center justify-between px-2.5 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-secondary"
              >
                <span>{FIELD_LABELS[field]}</span>
                {field === value.field && <Check className="h-3.5 w-3.5 text-primary" />}
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => onChange({ field: value.field, dirIndex: value.dirIndex === 0 ? 1 : 0 })}
          className="mt-1 flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
          from {directionLabel}
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
